import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';

import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { MatTooltipModule } from '@angular/material/tooltip';

import type { ApiCategory, ApiConversation, ApiMessage, ApiOrder, ApiOrderDocument, ApiOrderStatusHistoryEntry, ApiProvider, HandoffRow, OrderStatus, PaymentType, ServiceModel } from '../../core/api/api.types';
import { AttachmentsApiService } from '../../core/api/attachments-api.service';
import { ConversationsApiService } from '../../core/api/conversations-api.service';
import { HandoffApiService } from '../../core/api/handoff-api.service';
import { OrdersApiService } from '../../core/api/orders-api.service';
import type { CreateOrderPayload, UpdateOrderPayload } from '../../core/api/orders-api.service';
import { ProvidersApiService } from '../../core/api/providers-api.service';
import { ReferenceApiService } from '../../core/api/reference-api.service';
import { CaseUnreadService } from '../../core/case-unread/case-unread.service';
import { NotificationSoundService } from '../../core/notification-sound/notification-sound.service';
import { OperatorSocketService } from '../../core/socket/operator-socket.service';
import { CaseChatComponent } from './components/case-chat/case-chat.component';
import { CaseListComponent } from './components/case-list/case-list.component';
import { CaseSidebarComponent } from './components/case-sidebar/case-sidebar.component';
import { OrderDraftPillComponent } from './components/order-draft-pill/order-draft-pill.component';
import { describeDraft, isDraftDirty } from './order-draft';
import { buildOrderSummary, type OrderSummaryLine } from './order-summary';
import { sortProvidersForDrawer } from './provider-drawer';
import type { CaseListItem, CreateOrderDraft, LiveOrder } from './my-cases.types';
import { ORDER_STATUS_OPTIONS, PAYMENT_TYPE_OPTIONS, SERVICE_MODEL_OPTIONS } from './my-cases.types';
import { HandoffSummaryCardComponent } from '../../shared/handoff-summary-card/handoff-summary-card.component';

const LAST_CASE_KEY = 'op_last_case_id';

function buildPreviewConversation(): ApiConversation {
  const now = new Date().toISOString();
  return {
    id: 'preview-conversation',
    client_user_id: 'a3f82c1d-0001-0000-0000-000000000001',
    status: 'OPEN',
    mode: 'HUMAN',
    city_code: 'TOR',
    assigned_admin_id: 'preview-operator',
    last_message_at: now,
    returned_to_ai_at: null,
    created_at: now,
    updated_at: now,
  };
}

function buildPreviewHandoff(): HandoffRow {
  return {
    id: 'handoff-preview-10345',
    conversation_id: 'preview-conversation',
    city_code: 'TOR',
    status: 'TAKEN',
    category: 'Home Repairs',
    reason: 'Sink leak under kitchen counter',
    ai_summary: {
      summaryText: 'Sink is leaking under the kitchen counter. Client needs a same-day plumber.',
      categoryId: 'home_plumbing',
    },
    suggested_category_id: 'home_plumbing',
    suggested_providers: [],
    previous_provider: null,
    created_at: new Date().toISOString(),
    taken_by_admin_id: 'preview-operator',
    taken_at: new Date().toISOString(),
    customer_id: 'a3f82c1d-0001-0000-0000-000000000001',
    customer_preferred_name: 'Anna K.',
    customer_phone: '+1 (416) 555-0100',
    customer_address: null,
  };
}

function buildPreviewMessages(): ApiMessage[] {
  const now = Date.now();
  return [
    {
      id: 'msg-preview-1',
      conversation_id: 'preview-conversation',
      sender_type: 'USER',
      sender_user_id: 'customer-anna',
      text: 'Hi, our sink is leaking. We need someone to fix it ASAP!',
      meta: null,
      created_at: new Date(now - 22 * 60_000).toISOString(),
    },
    {
      id: 'msg-preview-2',
      conversation_id: 'preview-conversation',
      sender_type: 'AI',
      sender_user_id: null,
      text: 'Got it! Connecting you with an operator who can help arrange a plumber.',
      meta: null,
      created_at: new Date(now - 16 * 60_000).toISOString(),
    },
    {
      id: 'msg-preview-3',
      conversation_id: 'preview-conversation',
      sender_type: 'OPERATOR',
      sender_user_id: 'preview-operator',
      text: "Thanks for reaching out, Anna! I'll get a plumber assigned right away.",
      meta: null,
      created_at: new Date(now - 12 * 60_000).toISOString(),
    },
  ];
}

function buildPreviewOrders(): LiveOrder[] {
  const base: ApiOrder = {
    id: 'preview-order-1',
    order_code: 'ORD-10345',
    conversation_id: 'preview-conversation',
    handoff_request_id: 'handoff-preview-10345',
    created_by_admin_id: 'preview-operator',
    city_code: 'TOR',
    service_model: 'referral_dispatch',
    status: 'finding_provider',
    category: 'Home Repairs',
    service_name: 'Plumbing Repair',
    scheduled_at: null,
    scheduled_timezone: 'America/Toronto',
    address: null,
    address_access_notes: 'Access confirmed by phone. Client will be on site.',
    internal_notes: 'Customer confirmed 1–3 PM window. Urgent.',
    provider_assignment_type: 'unassigned',
    provider_id: null,
    provider_notes: null,
    providers: null,
    payment_provider: 'stripe',
    payment_status: 'pending',
    payment_type: null,
    payment_amount_cents: null,
    payment_currency: 'CAD',
    stripe_payment_link_id: null,
    stripe_payment_link_url: null,
    stripe_customer_id: null,
    payment_requested_at: null,
    payment_paid_at: null,
    payment_last_synced_at: null,
    quote_amount_cents: null,
    estimated_duration_minutes: null,
    completion_notes: null,
    cancelled_reason: null,
    assigned_at: null,
    confirmed_at: null,
    completed_at: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  return [{ ...base, editMode: true, providerDisplayText: '', scheduledDateObj: null, scheduledTimeStr: '', scheduledTimeDateObj: null }];
}

function extractServiceName(summaryText: string | null | undefined): string {
  if (!summaryText) return '';
  const match = summaryText.match(/\bService:\s*([^.]+)/i);
  return match ? match[1].trim() : '';
}

function buildDefaultDraft(handoff: HandoffRow | null): CreateOrderDraft {
  return {
    service_model: '',
    category: handoff?.category || '',
    service_name: extractServiceName(handoff?.ai_summary?.summaryText),
    scheduledDate: null,
    scheduledTime: null,
    providerDisplayText: '',
    provider_assignment_type: 'unassigned',
    provider_id: null,
    address: handoff?.customer_address || '',
    address_access_notes: '',
    internal_notes: '',
    payment_type: '',
    quote_amount: null,
    estimated_duration_minutes: null,
    completion_notes: '',
    cancelled_reason: '',
  };
}

function buildDraftFromOrder(order: LiveOrder): CreateOrderDraft {
  return {
    service_model: order.service_model,
    category: order.category ?? '',
    service_name: order.service_name ?? '',
    scheduledDate: order.scheduledDateObj,
    scheduledTime: order.scheduledTimeDateObj,
    providerDisplayText: order.providerDisplayText,
    provider_assignment_type: order.provider_assignment_type,
    provider_id: order.provider_id,
    address: order.address ?? '',
    address_access_notes: order.address_access_notes ?? '',
    internal_notes: order.internal_notes ?? '',
    payment_type: order.payment_type ?? '',
    quote_amount: order.quote_amount_cents == null ? null : order.quote_amount_cents / 100,
    estimated_duration_minutes: order.estimated_duration_minutes,
    completion_notes: order.completion_notes ?? '',
    cancelled_reason: order.cancelled_reason ?? '',
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCaseCode(customerId: string | null | undefined): string {
  if (!customerId) return '—';
  const compact = customerId.replace(/-/g, '').toUpperCase();
  return `CUST-${compact.slice(0, 5)}`;
}

function buildScheduledAt(date: Date | null, timeStr: string): string | null {
  if (!date) return null;
  const [h, m] = (timeStr || '00:00').split(':').map(Number);
  const d = new Date(date);
  d.setHours(h ?? 0, m ?? 0, 0, 0);
  return d.toISOString();
}

function buildScheduledAtFromPickers(date: Date | null, time: Date | null): string | null {
  if (!date) return null;
  const combined = new Date(date);
  if (time) combined.setHours(time.getHours(), time.getMinutes(), 0, 0);
  else combined.setHours(0, 0, 0, 0);
  return combined.toISOString();
}

function parseScheduledDate(iso: string | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

function parseScheduledTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function dollarsToCents(value: number | null | undefined): number | undefined {
  if (value === null || value === undefined || Number.isNaN(value)) return undefined;
  if (value < 0) return undefined;
  return Math.round(value * 100);
}

function providerDisplayText(order: ApiOrder, providers: ApiProvider[]): string {
  if (order.provider_assignment_type === 'assigned' && order.provider_id) {
    const p = providers.find((pr) => pr.id === order.provider_id);
    return p?.name ?? order.providers?.name ?? '';
  }
  if (order.provider_assignment_type === 'unknown') return 'Unknown';
  if (order.provider_assignment_type === 'other_in_notes') return 'Other (in notes)';
  return '';
}

function parseScheduledTimeDateObj(iso: string | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const t = new Date();
  t.setHours(d.getHours(), d.getMinutes(), 0, 0);
  return t;
}

function mapApiOrderToLive(order: ApiOrder, providers: ApiProvider[]): LiveOrder {
  return {
    ...order,
    editMode: false,
    providerDisplayText: providerDisplayText(order, providers),
    scheduledDateObj: parseScheduledDate(order.scheduled_at),
    scheduledTimeStr: parseScheduledTime(order.scheduled_at),
    scheduledTimeDateObj: parseScheduledTimeDateObj(order.scheduled_at),
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

@Component({
  selector: 'op-my-cases-page',
  imports: [
    DatePipe,
    RouterModule,
    FormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatDatepickerModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatSnackBarModule,
    MatTimepickerModule,
    MatTooltipModule,
    CaseListComponent,
    CaseChatComponent,
    CaseSidebarComponent,
    OrderDraftPillComponent,
    HandoffSummaryCardComponent,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './my-cases.page.html',
  styleUrl: './my-cases.page.scss',
})
export class MyCasesPage implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly convApi = inject(ConversationsApiService);
  private readonly handoffApi = inject(HandoffApiService);
  private readonly ordersApi = inject(OrdersApiService);
  private readonly providersApi = inject(ProvidersApiService);
  private readonly referenceApi = inject(ReferenceApiService);
  private readonly attachmentsApi = inject(AttachmentsApiService);
  private readonly socket = inject(OperatorSocketService);
  private readonly notificationSound = inject(NotificationSoundService);
  private readonly snack = inject(MatSnackBar);

  // ── Core state ──────────────────────────────────────────────────────────────
  readonly conversation = signal<ApiConversation | null>(null);
  readonly handoff = signal<HandoffRow | null>(null);
  readonly messages = signal<ApiMessage[]>([]);
  readonly loading = signal(false);
  readonly demoMode = signal(false);

  // ── Cases list (left panel) ──────────────────────────────────────────────────
  readonly myCases = signal<HandoffRow[]>([]);
  readonly casesLoading = signal(false);
  readonly activeHandoffId = signal<string | null>(null);
  readonly searchQuery = signal('');
  private readonly caseUnread = inject(CaseUnreadService);

  // ── Orders (right panel accordion) ──────────────────────────────────────────
  readonly orders = signal<LiveOrder[]>([]);
  readonly ordersLoading = signal(false);
  readonly ordersSaving = signal<Record<string, boolean>>({});

  // ── Status history (per-order timeline, lazy loaded when panel opens) ──────
  readonly orderHistory = signal<Record<string, ApiOrderStatusHistoryEntry[]>>({});
  readonly orderHistoryLoading = signal<Record<string, boolean>>({});

  // ── Documents (per-order, lazy loaded when panel opens) ────────────────────
  readonly orderDocuments = signal<Record<string, ApiOrderDocument[]>>({});
  readonly orderDocumentsLoading = signal<Record<string, boolean>>({});
  readonly orderDocumentsUploading = signal<Record<string, boolean>>({});
  readonly orderDocumentsError = signal<Record<string, string | null>>({});

  // ── Create order drawer ──────────────────────────────────────────────────────
  /**
   * `minimized` keeps every bit of drawer state alive — draft, edit target,
   * idempotency key, loaded history — while hiding the modal so the operator can
   * reach the chat and ask the client a question mid-order.
   */
  readonly createOrderDrawerState = signal<'closed' | 'open' | 'minimized'>('closed');
  readonly createOrderDrawerOpen = computed(() => this.createOrderDrawerState() === 'open');
  readonly createOrderDrawerMinimized = computed(
    () => this.createOrderDrawerState() === 'minimized',
  );
  readonly createOrderSaving = signal(false);
  readonly createOrderError = signal<string | null>(null);
  readonly editingOrder = signal<LiveOrder | null>(null);
  createOrderDraft: CreateOrderDraft = buildDefaultDraft(null);
  /** O6: idempotency key for the current create-order draft (new key per order). */
  private createOrderRequestId: string | null = null;
  /** What the drawer was seeded with, so "did the operator change anything?" is answerable. */
  private draftBaseline: CreateOrderDraft = buildDefaultDraft(null);

  readonly draftLabel = computed(() => describeDraft(this.editingOrder()));

  /**
   * A method rather than a computed: `createOrderDraft` is a plain object bound
   * with ngModel, not a signal, so there is nothing for a computed to track.
   * Change detection re-runs this the same way it already does for
   * `providerDrawerResults()`, and it only matters while the drawer is in play.
   */
  draftDirty(): boolean {
    return isDraftDirty(this.createOrderDraft, this.draftBaseline);
  }

  /**
   * True while the drawer is coming back from the pill, so it can fly out of the
   * corner it was sucked into. Opening from the sidebar has no corner to come
   * from and keeps the plain fade.
   */
  readonly createOrderDrawerRestoring = signal(false);

  /** Case the operator tried to switch to while holding unsaved draft work. */
  readonly pendingCaseSwitch = signal<string | null>(null);
  /** True when the operator asked to discard a dirty draft and must confirm. */
  readonly discardConfirmOpen = signal(false);

  // ── Providers & categories ───────────────────────────────────────────────────
  readonly providers = signal<ApiProvider[]>([]);
  readonly recommendedProviderId = signal<string | null>(null);
  readonly providersCategoryMissing = signal(false);
  readonly categories = signal<ApiCategory[]>([]);

  // ── Return to AI ─────────────────────────────────────────────────────────────
  readonly returnModalOpen = signal(false);
  readonly returnConfirm1 = signal(false);
  readonly returnConfirm2 = signal(false);
  readonly returningToAi = signal(false);
  readonly returnToAiError = signal<string | null>(null);
  readonly returnConfirmed = computed(() => this.returnConfirm1() && this.returnConfirm2());

  // ── Order summary modal ──────────────────────────────────────────────────────
  readonly summaryOrder = signal<LiveOrder | null>(null);
  readonly summaryLines = computed<OrderSummaryLine[]>(() => {
    const order = this.summaryOrder();
    return order ? buildOrderSummary(order) : [];
  });

  // ── Unread ───────────────────────────────────────────────────────────────────
  readonly firstUnreadMessageId = signal<string | null>(null);
  private readonly lastSeenAtByConv = new Map<string, string>();

  // ── Mute / block ─────────────────────────────────────────────────────────────
  readonly muteCustomerAction = signal<string | null>(null);
  readonly pendingMuteAction = signal<string | null>(null);

  // ── Computed ─────────────────────────────────────────────────────────────────
  readonly customerName = computed(() => this.handoff()?.customer_preferred_name?.trim() || 'Customer');
  readonly customerPhone = computed(() => this.handoff()?.customer_phone?.trim() || '');

  readonly caseList = computed<CaseListItem[]>(() => {
    const handoffs = this.myCases();
    const activeId = this.activeHandoffId();
    const q = this.searchQuery().trim().toLowerCase();

    if (handoffs.length === 0) {
      return [];
    }

    const items = handoffs.map((h) => ({
      id: h.id,
      name: h.customer_preferred_name || this.handoffLabel(h),
      caseCode: this.primaryCaseCodeForHandoff(h),
      status: 'Active',
      initials: this.initialsFromName(h.customer_preferred_name || 'C'),
      selected: h.id === activeId,
      unreadCount: this.caseUnread.getCount(h.conversation_id),
    }));

    if (!q) return items;
    return items.filter(
      (item) => item.name.toLowerCase().includes(q) || item.caseCode.toLowerCase().includes(q),
    );
  });

  readonly muteConfirmMessage = computed(() => {
    const action = this.pendingMuteAction();
    const name = this.customerName();
    if (action === 'block') return `Do you really want to block ${name}?`;
    if (action === '15') return `Do you really want to mute ${name} for 15 minutes?`;
    if (action === '30') return `Do you really want to mute ${name} for 30 minutes?`;
    return null;
  });

  // ── Exposed constants ────────────────────────────────────────────────────────
  readonly orderStatusOptions = ORDER_STATUS_OPTIONS;
  readonly serviceModelOptions = SERVICE_MODEL_OPTIONS;
  readonly paymentTypeOptions = PAYMENT_TYPE_OPTIONS;

  protected activeConversationId: string | null = null;
  private readonly sub = new Subscription();
  private loadSequence = 0;

  // ── Lifecycle ─────────────────────────────────────────────────────────────────

  async ngOnInit(): Promise<void> {
    this.sub.add(
      this.socket.connected$.subscribe((ev) => {
        if (!ev.isReconnect || !this.activeConversationId) return;
        void this.handleReconnect(this.activeConversationId);
      }),
    );

    this.sub.add(
      this.socket.messageCreated$.subscribe((message) => {
        const isKnownCase = this.myCases().some(
          (h) => h.conversation_id === message.conversation_id,
        );
        if (!isKnownCase && message.conversation_id !== this.activeConversationId) return;

        if (message.conversation_id !== this.activeConversationId) {
          if (message.sender_type !== 'OPERATOR') {
            this.notificationSound.playNewMessage();
          }
          return;
        }
        this.messages.update((items) => this.mergeMessages(items, [message]));
      }),
    );

    this.sub.add(
      this.socket.orderStatusChanged$.subscribe((ev) => {
        if (ev.conversationId !== this.activeConversationId) return;
        this.orders.update((list) =>
          list.map((o) =>
            o.id === ev.orderId ? { ...o, status: ev.status as OrderStatus } : o,
          ),
        );
      }),
    );

    // An order on this case was changed elsewhere (another operator / SUPERADMIN).
    // Refetch that single order so views stay in sync — but never clobber an edit
    // the current operator is in the middle of.
    this.sub.add(
      this.socket.orderUpdated$.subscribe((ev) => {
        if (ev.conversationId !== this.activeConversationId) return;
        const order = this.orders().find((o) => o.id === ev.orderId);
        if (!order || order.editMode || this.ordersSaving()[ev.orderId]) return;
        void this.refreshOrder(ev.orderId);
      }),
    );

    try {
      await this.socket.connect();
    } catch (err) {
      console.warn('[MyCases] WS connect failed', err);
    }

    void this.loadMyCases();
    void this.loadCategories();
    // Providers are loaded per-case (city-scoped) in initializeCase — no need for a
    // second city-agnostic fetch here (P2/H3).

    this.sub.add(
      this.route.paramMap.subscribe((params) => {
        const handoffId = params.get('id');
        void this.initializeCase(handoffId);
      }),
    );
  }

  ngOnDestroy(): void {
    this.recordLastSeen();
    for (const id of this.socket.getJoinedConversationIds()) {
      this.socket.leaveConversation(id);
    }
    this.caseUnread.setActive(null);
    this.sub.unsubscribe();
  }

  // ── Navigation ────────────────────────────────────────────────────────────────

  selectCase(handoffId: string): void {
    // A draft belongs to the case it was started from, so switching drops it.
    // Ask first when there is work to lose — loading the next case would wipe it
    // without a trace otherwise.
    if (
      handoffId !== this.activeHandoffId() &&
      this.createOrderDrawerState() !== 'closed' &&
      this.draftDirty()
    ) {
      this.pendingCaseSwitch.set(handoffId);
      return;
    }

    void this.router.navigate(['/my-cases', handoffId]);
  }

  /** Keep the draft: stay on this case and put the drawer back in front. */
  cancelCaseSwitch(): void {
    this.pendingCaseSwitch.set(null);
    if (this.createOrderDrawerState() === 'minimized') {
      this.createOrderDrawerState.set('open');
    }
  }

  confirmCaseSwitch(): void {
    const handoffId = this.pendingCaseSwitch();
    this.pendingCaseSwitch.set(null);
    if (!handoffId) return;

    this.discardDraft();
    void this.router.navigate(['/my-cases', handoffId]);
  }

  // ── Display helpers ───────────────────────────────────────────────────────────

  primaryCaseCode(): string {
    return formatCaseCode(this.conversation()?.client_user_id) || '—';
  }

  customerInitials(): string {
    return this.initialsFromName(this.customerName());
  }

  pastOrdersRoute(): any[] {
    return this.activeConversationId
      ? ['/past-orders', { conversationId: this.activeConversationId }]
      : ['/past-orders'];
  }

  // ── Drawer: provider autocomplete ──────────────────────────────────────────

  providerDrawerResults(): ApiProvider[] {
    return sortProvidersForDrawer(
      this.providers(),
      this.recommendedProviderId(),
      this.createOrderDraft.providerDisplayText || '',
    );
  }

  isRecommendedProvider(provider: ApiProvider): boolean {
    return provider.id === this.recommendedProviderId();
  }

  onDrawerCategoryChange(): void {
    // The chosen category changes who may take the order — clear a now possibly
    // off-category selection rather than silently submitting it.
    this.createOrderDraft.provider_assignment_type = 'unassigned';
    this.createOrderDraft.provider_id = null;
    this.createOrderDraft.providerDisplayText = '';
    void this.loadProviders();
  }

  selectDrawerProvider(provider: ApiProvider): void {
    this.createOrderDraft.provider_assignment_type = 'assigned';
    this.createOrderDraft.provider_id = provider.id;
    this.createOrderDraft.providerDisplayText = provider.name;
  }

  applyDrawerProviderShortcut(value: 'Assign later' | 'Unknown' | 'Other (in notes)'): void {
    if (value === 'Unknown') {
      this.createOrderDraft.provider_assignment_type = 'unknown';
      this.createOrderDraft.provider_id = null;
      this.createOrderDraft.providerDisplayText = 'Unknown';
    } else if (value === 'Other (in notes)') {
      this.createOrderDraft.provider_assignment_type = 'other_in_notes';
      this.createOrderDraft.provider_id = null;
      this.createOrderDraft.providerDisplayText = 'Other (in notes)';
    } else {
      this.createOrderDraft.provider_assignment_type = 'unassigned';
      this.createOrderDraft.provider_id = null;
      this.createOrderDraft.providerDisplayText = '';
    }
  }

  // ── Create order drawer ───────────────────────────────────────────────────────

  openCreateOrderDrawer(): void {
    // Draft is intentionally NOT reset here — preserve any data the operator
    // already entered if the drawer was closed accidentally.
    this.createOrderError.set(null);
    if (this.createOrderDrawerState() === 'closed') {
      this.draftBaseline = buildDefaultDraft(this.handoff());
    }
    this.createOrderDrawerRestoring.set(false);
    this.createOrderDrawerState.set('open');
    void this.loadCategories();
  }

  openEditOrderDrawer(order: LiveOrder): void {
    this.editingOrder.set(order);
    this.createOrderDraft = buildDraftFromOrder(order);
    this.draftBaseline = buildDraftFromOrder(order);
    this.createOrderError.set(null);
    this.createOrderDrawerRestoring.set(false);
    this.createOrderDrawerState.set('open');
    void this.loadCategories();
    void this.loadOrderHistory(order.id);
    void this.loadOrderDocuments(order.id);
  }

  /**
   * Park the drawer without touching a single field. This is what makes it safe
   * to step away mid-order: the draft, the order being edited, the idempotency
   * key and the loaded history all stay exactly as they were.
   */
  minimizeCreateOrderDrawer(): void {
    if (this.createOrderDrawerState() !== 'open') return;
    this.createOrderDrawerRestoring.set(false);
    this.createOrderDrawerState.set('minimized');
  }

  resumeCreateOrderDrawer(): void {
    if (this.createOrderDrawerState() !== 'minimized') return;
    this.createOrderDrawerRestoring.set(true);
    this.createOrderDrawerState.set('open');
  }

  /**
   * Explicit "throw this away". Unsaved work asks first — this button sits one
   * click from a filled-in form, and losing it silently is the failure mode the
   * panel is meant to avoid.
   */
  requestDiscardDraft(): void {
    if (this.draftDirty()) {
      this.discardConfirmOpen.set(true);
      return;
    }
    this.discardDraft();
  }

  cancelDiscardDraft(): void {
    this.discardConfirmOpen.set(false);
  }

  confirmDiscardDraft(): void {
    this.discardConfirmOpen.set(false);
    this.discardDraft();
  }

  /** Drop the drawer and everything in it. */
  discardDraft(): void {
    this.createOrderDrawerState.set('closed');
    this.editingOrder.set(null);
    this.createOrderError.set(null);
    this.resetOrderDraft();
  }

  async saveEditOrder(): Promise<void> {
    const order = this.editingOrder();
    if (!order) return;
    if (this.demoMode()) {
      this.discardDraft();
      return;
    }

    const draft = this.createOrderDraft;
    if (!draft.service_model) {
      this.createOrderError.set('Service model is required.');
      return;
    }

    this.createOrderSaving.set(true);
    this.createOrderError.set(null);
    this.setOrderSaving(order.id, true);
    try {
      const payload: UpdateOrderPayload = {
        service_model: draft.service_model as ServiceModel,
        category: draft.category || undefined,
        service_name: draft.service_name || undefined,
        scheduled_at: buildScheduledAtFromPickers(draft.scheduledDate, draft.scheduledTime),
        address: draft.address || undefined,
        address_access_notes: draft.address_access_notes || undefined,
        internal_notes: draft.internal_notes || undefined,
        payment_type: draft.payment_type || undefined,
        quote_amount_cents: dollarsToCents(draft.quote_amount),
        estimated_duration_minutes:
          draft.estimated_duration_minutes && draft.estimated_duration_minutes > 0
            ? draft.estimated_duration_minutes
            : undefined,
        completion_notes: draft.completion_notes || undefined,
        cancelled_reason: draft.cancelled_reason || undefined,
        expected_updated_at: order.updated_at, // O7: optimistic concurrency
      };

      let updated = await this.ordersApi.updateOrder(order.id, payload);

      if (
        order.provider_assignment_type !== draft.provider_assignment_type ||
        order.provider_id !== draft.provider_id
      ) {
        updated = await this.ordersApi.assignProvider(order.id, {
          provider_assignment_type: draft.provider_assignment_type,
          provider_id: draft.provider_id ?? undefined,
        });
      }

      const live = mapApiOrderToLive(updated, this.providers());
      this.orders.update((prev) => prev.map((o) => (o.id === order.id ? live : o)));
      this.createOrderDrawerState.set('closed');
      this.editingOrder.set(null);
      this.resetOrderDraft();
    } catch (err: any) {
      console.warn('[MyCases] edit order failed', err);
      this.createOrderError.set(err?.message ?? 'Failed to save order. Please try again.');
    } finally {
      this.createOrderSaving.set(false);
      this.setOrderSaving(order.id, false);
    }
  }

  async saveCreateOrder(): Promise<void> {
    if (this.demoMode()) {
      this.createOrderDrawerState.set('closed');
      return;
    }

    const draft = this.createOrderDraft;
    if (!draft.service_model) {
      this.createOrderError.set('Service model is required.');
      return;
    }

    const conversationId = this.activeConversationId;
    const handoff = this.handoff();
    if (!conversationId) return;

    this.createOrderSaving.set(true);
    this.createOrderError.set(null);

    try {
      // O6: one atomic create call (provider + notes included), with an
      // idempotency key so a retry / double-submit cannot create a duplicate.
      const payload: CreateOrderPayload = {
        conversation_id: conversationId,
        handoff_request_id: handoff?.id ?? null,
        service_model: draft.service_model as ServiceModel,
        category: draft.category || undefined,
        service_name: draft.service_name || undefined,
        scheduled_at: buildScheduledAtFromPickers(draft.scheduledDate, draft.scheduledTime),
        address: draft.address || undefined,
        address_access_notes: draft.address_access_notes || undefined,
        internal_notes: draft.internal_notes || undefined,
        payment_type: draft.payment_type || undefined,
        quote_amount_cents: dollarsToCents(draft.quote_amount),
        estimated_duration_minutes:
          draft.estimated_duration_minutes && draft.estimated_duration_minutes > 0
            ? draft.estimated_duration_minutes
            : undefined,
        provider_assignment_type: draft.provider_assignment_type,
        provider_id:
          draft.provider_assignment_type === 'assigned' ? (draft.provider_id ?? undefined) : undefined,
        completion_notes: draft.completion_notes || undefined,
        cancelled_reason: draft.cancelled_reason || undefined,
        client_request_id: (this.createOrderRequestId ??= crypto.randomUUID()),
      };

      const created = await this.ordersApi.createOrder(payload);

      const live = mapApiOrderToLive(created, this.providers());
      live.editMode = true;

      this.orders.update((prev) => [live, ...prev]);
      this.createOrderDrawerState.set('closed');
      this.resetOrderDraft();
    } catch (err: any) {
      console.warn('[MyCases] create order failed', err);
      this.createOrderError.set(err?.message ?? 'Failed to create order. Please try again.');
    } finally {
      this.createOrderSaving.set(false);
    }
  }

  async saveOrderStatus(order: LiveOrder, newStatus: OrderStatus): Promise<void> {
    if (this.demoMode()) {
      order.status = newStatus;
      return;
    }

    this.setOrderSaving(order.id, true);
    try {
      const updated = await this.ordersApi.updateOrderStatus(
        order.id,
        newStatus,
        undefined,
        order.updated_at, // O7: optimistic concurrency
      );
      const live = mapApiOrderToLive(updated, this.providers());
      live.editMode = order.editMode;
      this.orders.update((prev) => prev.map((o) => (o.id === order.id ? live : o)));
      void this.loadOrderHistory(order.id, true);
    } catch (err: any) {
      console.warn('[MyCases] status update failed', err);
      this.onOrderSaveError(order.id, err, 'Failed to update status. Please try again.');
    } finally {
      this.setOrderSaving(order.id, false);
    }
  }

  /**
   * Shared error handling for order saves. On a 409 (the order was modified
   * elsewhere — O7) we reload the latest version instead of clobbering it;
   * otherwise we surface the message and leave the row as-is for a retry.
   */
  private onOrderSaveError(orderId: string, err: any, fallback: string): void {
    if (err?.status === 409) {
      this.snack.open('This order was changed elsewhere — reloading the latest version.', 'Dismiss', {
        duration: 4500,
      });
      void this.refreshOrder(orderId);
      return;
    }
    this.snack.open(err?.message ?? fallback, 'Dismiss', { duration: 4000 });
  }

  canSendPayment(order: LiveOrder): boolean {
    if (!order.payment_type) return false;
    return (order.payment_amount_cents ?? order.quote_amount_cents ?? 0) > 0;
  }

  async sendPayment(order: LiveOrder): Promise<void> {
    if (this.demoMode()) return;

    const amount = order.payment_amount_cents ?? order.quote_amount_cents ?? 0;
    if (!order.payment_type || amount <= 0) {
      this.setOrderDocumentError(order.id, 'Set payment type and a quote amount before sending payment.');
      return;
    }

    this.setOrderSaving(order.id, true);
    this.setOrderDocumentError(order.id, null);
    try {
      const updated = await this.ordersApi.sendPayment(order.id);
      const live = mapApiOrderToLive(updated, this.providers());
      live.editMode = order.editMode;
      this.orders.update((prev) => prev.map((o) => (o.id === order.id ? live : o)));
    } catch (err: any) {
      console.warn('[MyCases] sendPayment failed', err);
      this.setOrderDocumentError(order.id, err?.message ?? 'Failed to generate payment link. Please try again.');
    } finally {
      this.setOrderSaving(order.id, false);
    }
  }

  async copyPaymentLink(url: string | null): Promise<void> {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Clipboard may be blocked; link is visible in UI for manual copy.
    }
  }

  async onInvoiceSelected(order: LiveOrder, event: Event): Promise<void> {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (input) input.value = '';
    if (!file) return;

    if (this.demoMode()) return;

    this.setOrderDocumentError(order.id, null);
    this.orderDocumentsUploading.update((map) => ({ ...map, [order.id]: true }));

    try {
      const doc = await this.ordersApi.uploadDocument(order.id, file, 'invoice_copy');
      this.orderDocuments.update((map) => ({
        ...map,
        [order.id]: [doc, ...(map[order.id] ?? [])],
      }));
    } catch (err: any) {
      console.warn('[MyCases] document upload failed', err);
      this.setOrderDocumentError(order.id, err?.message ?? 'Failed to upload document.');
    } finally {
      this.orderDocumentsUploading.update((map) => {
        const next = { ...map };
        delete next[order.id];
        return next;
      });
    }
  }

  async openDocument(doc: ApiOrderDocument): Promise<void> {
    if (this.demoMode()) return;
    try {
      const { url } = await this.ordersApi.documentSignedUrl(doc.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      console.warn('[MyCases] document open failed', err);
      this.setOrderDocumentError(doc.order_id, err?.message ?? 'Failed to open document.');
    }
  }

  documentsFor(orderId: string): ApiOrderDocument[] {
    return this.orderDocuments()[orderId] ?? [];
  }

  historyFor(orderId: string): ApiOrderStatusHistoryEntry[] {
    return this.orderHistory()[orderId] ?? [];
  }

  async removeDocument(doc: ApiOrderDocument): Promise<void> {
    if (this.demoMode()) return;
    const confirmed = window.confirm(`Remove "${doc.original_filename}"?`);
    if (!confirmed) return;

    try {
      await this.ordersApi.deleteDocument(doc.id);
      this.orderDocuments.update((map) => ({
        ...map,
        [doc.order_id]: (map[doc.order_id] ?? []).filter((d) => d.id !== doc.id),
      }));
    } catch (err: any) {
      console.warn('[MyCases] document delete failed', err);
      this.setOrderDocumentError(doc.order_id, err?.message ?? 'Failed to remove document.');
    }
  }

  // ── Chat events ───────────────────────────────────────────────────────────────

  onDemoMessageSent(msg: ApiMessage): void {
    this.messages.update((items) => [...items, msg]);
  }

  // ── Mute / block ─────────────────────────────────────────────────────────────

  onMuteSelectionChange(value: string | null): void {
    this.pendingMuteAction.set(value);
  }

  confirmMuteAction(): void {
    const action = this.pendingMuteAction() as '15' | '30' | 'block' | null;
    this.pendingMuteAction.set(null);
    if (!action) return;

    if (this.demoMode()) {
      this.muteCustomerAction.set(action);
      return;
    }

    const conversationId = this.activeConversationId;
    if (!conversationId) return;

    this.muteCustomerAction.set(action);
    this.convApi.muteConversation(conversationId, action).catch((err) => {
      console.warn('[MyCases] mute failed', err);
    });
  }

  cancelMuteAction(): void {
    this.pendingMuteAction.set(null);
  }

  // ── Return to AI ─────────────────────────────────────────────────────────────

  openReturnToAiModal(): void {
    this.returnConfirm1.set(false);
    this.returnConfirm2.set(false);
    this.returnToAiError.set(null);
    this.returnModalOpen.set(true);
  }

  closeReturnToAiModal(): void {
    if (this.returningToAi()) return;
    this.returnModalOpen.set(false);
  }

  // ── Order summary modal ──────────────────────────────────────────────────────

  openOrderSummary(order: LiveOrder): void {
    this.summaryOrder.set(order);
  }

  closeOrderSummary(): void {
    this.summaryOrder.set(null);
  }

  async returnToAi(): Promise<void> {
    if (!this.returnConfirmed() || this.returningToAi()) return;

    const conversationId = this.activeConversationId;
    const handoffId = this.activeHandoffId();
    if (!conversationId) return;

    this.returningToAi.set(true);
    this.returnToAiError.set(null);

    try {
      await this.convApi.returnToAi(conversationId);
      this.returnModalOpen.set(false);
      this.socket.leaveConversation(conversationId);
      this.caseUnread.forget(conversationId); // M2: drop unread state for the closed case

      const remaining = this.myCases().filter((h) => h.id !== handoffId);
      this.myCases.set(remaining);

      if (remaining.length > 0) {
        void this.router.navigate(['/my-cases', remaining[0].id], { replaceUrl: true });
      } else {
        void this.router.navigate(['/my-cases'], { replaceUrl: true });
      }
    } catch (err: any) {
      console.warn('[MyCases] returnToAi failed', err);
      this.returnToAiError.set(err?.message ?? 'Failed to return conversation to AI. Please try again.');
    } finally {
      this.returningToAi.set(false);
    }
  }

  // ── Private: loading ──────────────────────────────────────────────────────────

  private async loadMyCases(): Promise<void> {
    this.casesLoading.set(true);
    try {
      const firstPage = await this.handoffApi.inbox({ status: 'TAKEN', page: 1, pageSize: 100 });
      const cases = [...(firstPage.items ?? [])].sort((a, b) =>
        (b.taken_at ?? b.created_at ?? '').localeCompare(a.taken_at ?? a.created_at ?? ''),
      );
      this.myCases.set(cases);

      // Join all conversation rooms so background messages arrive for unread counting.
      for (const c of cases) {
        void this.socket.joinConversation(c.conversation_id);
      }

      if (!this.activeHandoffId() && cases.length > 0) {
        const lastId = sessionStorage.getItem(LAST_CASE_KEY);
        const lastCase = lastId ? cases.find((c) => c.id === lastId) : undefined;
        const target = lastCase ?? cases[0];
        void this.router.navigate(['/my-cases', target.id], { replaceUrl: true });
      }
    } catch (err) {
      console.warn('[MyCases] failed to load cases list', err);
    } finally {
      this.casesLoading.set(false);
    }
  }

  /**
   * Load the providers the operator may assign for this case.
   *
   * The backend now applies the category filter authoritatively — an off-category
   * provider is never offered. `createOrderDraft.category` holds a category
   * *name* (the drawer's mat-select binds `cat.name`, matching what
   * `orders.category` stores), so it is resolved back to an id here.
   */
  private async loadProviders(): Promise<void> {
    try {
      const cityCode = this.conversation()?.city_code;
      const categoryId = this.draftCategoryId();

      const { items, recommended_provider_id } = await this.providersApi.list({
        cityCodes: cityCode ? [cityCode] : undefined,
        categoryIds: categoryId ? [categoryId] : undefined,
        isActive: 'true',
      });

      this.providers.set(items);
      this.recommendedProviderId.set(recommended_provider_id);
      this.providersCategoryMissing.set(!!categoryId && items.length === 0);
    } catch (err) {
      console.warn('[MyCases] failed to load providers', err);
      this.providers.set([]);
      this.recommendedProviderId.set(null);
      this.providersCategoryMissing.set(false);
    }
  }

  /** Resolve the drafted category name to its catalog id, if we know it. */
  private draftCategoryId(): string | null {
    const name = (this.createOrderDraft.category || '').trim();
    if (name) {
      const match = this.categories().find((c) => c.name === name);
      if (match) return match.category_id;
    }
    return this.handoff()?.suggested_category_id ?? null;
  }

  private async loadCategories(): Promise<void> {
    if (this.categories().length) return;
    try {
      const { items } = await this.referenceApi.categories();
      this.categories.set(items);
    } catch (err) {
      console.warn('[MyCases] failed to load categories', err);
    }
  }

  private async loadOrders(conversationId: string): Promise<void> {
    this.ordersLoading.set(true);
    try {
      const { items } = await this.ordersApi.listOrdersForConversation(conversationId);
      const live = items.map((o) => mapApiOrderToLive(o, this.providers()));
      this.orders.set(live);
      this.orderHistory.set({});
      this.orderDocuments.set({});
      this.orderDocumentsError.set({});
      if (live[0]) {
        void this.loadOrderHistory(live[0].id);
        void this.loadOrderDocuments(live[0].id);
      }
    } catch (err) {
      console.warn('[MyCases] failed to load orders', err);
      this.orders.set([]);
    } finally {
      this.ordersLoading.set(false);
    }
  }

  /** Refetch a single order (already in the loaded list) and replace it in place. */
  private async refreshOrder(orderId: string): Promise<void> {
    if (this.demoMode()) return;
    try {
      const updated = await this.ordersApi.getOrder(orderId);
      const live = mapApiOrderToLive(updated, this.providers());
      this.orders.update((prev) => prev.map((o) => (o.id === orderId ? live : o)));
    } catch (err) {
      console.warn('[MyCases] refreshOrder failed', err);
    }
  }

  private async loadOrderHistory(orderId: string, force = false): Promise<void> {
    if (this.demoMode()) return;
    if (!force && this.orderHistory()[orderId]) return;
    if (!force && this.orderHistoryLoading()[orderId]) return;

    this.orderHistoryLoading.update((map) => ({ ...map, [orderId]: true }));
    try {
      const { items } = await this.ordersApi.listStatusHistory(orderId);
      this.orderHistory.update((map) => ({ ...map, [orderId]: items }));
    } catch (err) {
      console.warn('[MyCases] load order history failed', err);
    } finally {
      this.orderHistoryLoading.update((map) => {
        const next = { ...map };
        delete next[orderId];
        return next;
      });
    }
  }

  private async loadOrderDocuments(orderId: string, force = false): Promise<void> {
    if (this.demoMode()) return;
    if (!force && this.orderDocuments()[orderId]) return;
    if (this.orderDocumentsLoading()[orderId]) return;

    this.orderDocumentsLoading.update((map) => ({ ...map, [orderId]: true }));
    try {
      const { items } = await this.ordersApi.listDocuments(orderId);
      this.orderDocuments.update((map) => ({ ...map, [orderId]: items }));
    } catch (err) {
      console.warn('[MyCases] load documents failed', err);
    } finally {
      this.orderDocumentsLoading.update((map) => {
        const next = { ...map };
        delete next[orderId];
        return next;
      });
    }
  }

  private async initializeCase(handoffId: string | null): Promise<void> {
    const sequence = ++this.loadSequence;

    this.recordLastSeen();
    this.firstUnreadMessageId.set(null);
    // A drawer or pill must never survive into another case: the draft is reset
    // below, and leaving `editingOrder` set would point the form at an order
    // belonging to the case we just left.
    this.createOrderDrawerState.set('closed');
    this.editingOrder.set(null);
    this.createOrderError.set(null);
    this.pendingCaseSwitch.set(null);
    this.discardConfirmOpen.set(false);
    this.returnModalOpen.set(false);
    this.returnConfirm1.set(false);
    this.returnConfirm2.set(false);
    this.returnToAiError.set(null);
    this.activeHandoffId.set(handoffId);

    if (!handoffId) {
      const firstCase = this.myCases()[0];
      if (firstCase) {
        void this.router.navigate(['/my-cases', firstCase.id], { replaceUrl: true });
        return;
      }
      if (!this.demoMode()) {
        this.activeConversationId = null;
        this.loading.set(false);
        return;
      }
      this.usePreviewState();
      return;
    }

    this.demoMode.set(false);
    this.loading.set(true);
    sessionStorage.setItem(LAST_CASE_KEY, handoffId);

    try {
      const { handoff } = await this.handoffApi.getById(handoffId);

      if (sequence !== this.loadSequence) return;
      if (!handoff) {
        if (sequence === this.loadSequence) this.usePreviewState();
        return;
      }

      this.handoff.set(handoff);
      const conversationId = handoff.conversation_id;

      this.activeConversationId = conversationId;

      this.caseUnread.setActive(conversationId);

      try {
        await this.socket.joinConversation(conversationId);
      } catch (err) {
        console.warn('[MyCases] join failed', err);
      }

      const [conversation, messages] = await Promise.all([
        this.convApi.getConversation(conversationId),
        this.convApi.listMessages(conversationId, { limit: 100 }),
      ]);

      if (sequence !== this.loadSequence) return;

      this.conversation.set(conversation);
      const orderedMessages = [...(messages.items ?? [])].reverse();
      this.messages.set(orderedMessages);

      const lastSeen = this.lastSeenAtByConv.get(conversationId);
      if (lastSeen) {
        const firstUnread = orderedMessages.find((m) => m.created_at > lastSeen);
        this.firstUnreadMessageId.set(firstUnread?.id ?? null);
      }

      if (sequence !== this.loadSequence) return;

      await Promise.all([
        this.loadOrders(conversationId),
        this.loadProviders(),
      ]);

      if (sequence !== this.loadSequence) return;
      this.resetOrderDraft();
    } catch (err) {
      console.warn('[MyCases] failed to load case', err);
      if (sequence !== this.loadSequence) return;
      this.usePreviewState();
    } finally {
      if (sequence === this.loadSequence) {
        this.loading.set(false);
      }
    }
  }

  private usePreviewState(): void {
    this.activeConversationId = null;
    this.demoMode.set(false);
    this.loading.set(false);
    this.conversation.set(null);
    this.handoff.set(null);
    this.messages.set([]);
    this.orders.set([]);
    this.resetOrderDraft();
  }

  private resetOrderDraft(): void {
    const handoff = this.handoff();
    this.createOrderDraft = buildDefaultDraft(handoff);
    // New draft → new idempotency key for the next order.
    this.createOrderRequestId = null;

    const suggested = handoff?.suggested_providers ?? [];
    // A provider the client asked for beats whose turn it is; both beat the
    // alphabetical first entry, which carries no meaning at all.
    const primary =
      suggested.find((p) => p.is_primary) ??
      suggested.find((p) => p.is_recommended) ??
      suggested[0] ??
      null;
    if (primary) {
      const matched = this.providers().find((p) => p.id === primary.id);
      this.createOrderDraft.provider_assignment_type = 'assigned';
      this.createOrderDraft.provider_id = primary.id;
      this.createOrderDraft.providerDisplayText = matched?.name ?? primary.name;
    }

    // The fresh draft is the new "nothing changed yet" reference point.
    this.draftBaseline = { ...this.createOrderDraft };
  }

  private setOrderSaving(orderId: string, saving: boolean): void {
    this.ordersSaving.update((map) => ({ ...map, [orderId]: saving }));
  }

  private setOrderDocumentError(orderId: string, message: string | null): void {
    this.orderDocumentsError.update((map) => ({ ...map, [orderId]: message }));
  }

  // ── Private: socket / reconnect ───────────────────────────────────────────────

  private async handleReconnect(conversationId: string): Promise<void> {
    try {
      await this.socket.joinConversation(conversationId);
    } catch (error) {
      console.warn('[MyCases] rejoin failed after reconnect', error);
    }
    try {
      await this.resyncMissedMessages(conversationId);
    } catch (error) {
      console.warn('[MyCases] resync failed after reconnect', error);
    }
  }

  private async resyncMissedMessages(conversationId: string): Promise<void> {
    const after = this.getLastServerCreatedAt();
    const response = await this.convApi.listMessages(conversationId, { limit: 100, after });
    const incoming = [...(response.items ?? [])].reverse();
    this.messages.update((items) => this.mergeMessages(items, incoming));
  }

  private getLastServerCreatedAt(): string | undefined {
    const items = this.messages();
    return items[items.length - 1]?.created_at;
  }

  private mergeMessages(current: ApiMessage[], incoming: ApiMessage[]): ApiMessage[] {
    const byId = new Map<string, ApiMessage>();
    for (const item of current) byId.set(item.id, item);
    for (const item of incoming) byId.set(item.id, item);
    return [...byId.values()].sort((a, b) => a.created_at.localeCompare(b.created_at));
  }

  // ── Private: unread tracking ──────────────────────────────────────────────────

  private recordLastSeen(): void {
    const convId = this.activeConversationId;
    if (!convId) return;
    const msgs = this.messages();
    const last = msgs[msgs.length - 1];
    if (last) {
      this.lastSeenAtByConv.set(convId, last.created_at);
    }
  }

  // ── Private: display ──────────────────────────────────────────────────────────

  private primaryCaseCodeForHandoff(h: HandoffRow): string {
    return formatCaseCode(h.customer_id);
  }

  private initialsFromName(value: string): string {
    const parts = value.split(' ').filter(Boolean);
    if (!parts.length) return 'C';
    return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() || '').join('');
  }

  private handoffLabel(h: HandoffRow): string {
    const source = (h.conversation_id || h.id || '').replace(/-/g, '').toUpperCase();
    const suffix = source.slice(-4);
    return suffix ? `Customer #${suffix}` : 'Customer';
  }
}
